import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, Phone, User } from 'lucide-react';
import { useRegistration } from '../../../../contexts/registration/hooks';
import { supabase } from '../../../../lib/supabase';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/auth/hooks';

// ✅ הוספות למדיניות
import { PrivacyConsent } from '../../../../components/PrivacyConsent';
import { hasLocalConsent, setLocalConsent, POLICY_VERSION } from '../../../../lib/policy';

interface BusinessStepOneProps {
  loading?: boolean;
}

export function BusinessStepOne({ loading }: BusinessStepOneProps) {
  const { updateStep, getStepData } = useRegistration();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState(() => {
    const saved = getStepData(1);
    return {
      name: saved?.name || '',
      email: saved?.email || '',
      password: saved?.password || '',
      phone: saved?.phone || ''
    };
  });

  const [errors, setErrors] = useState({
    name: '',
    email: '',
    password: '',
    phone: ''
  });

  // ✅ מצב אישור מדיניות (חובה לשלב זה)
  const [policyAccepted, setPolicyAccepted] = useState<boolean>(hasLocalConsent());

  // אם יש session – ננעל את האימייל ל-user.email כדי למנוע בלבול
  useEffect(() => {
    if (user?.email && formData.email !== user.email) {
      setFormData(prev => ({ ...prev, email: user.email! }));
      if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
    }
  }, [user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  // בדיקת סיסמה חזקה (לשימוש רק כשאין session)
  function isStrongPassword(password: string): boolean {
    const length = password.length >= 6;
    const lower = /[a-z]/.test(password);
    const upper = /[A-Z]/.test(password);
    const digit = /[0-9]/.test(password);
    const special = /[!@#$%^&*()_\+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password);
    return length && lower && upper && digit && special;
  }

  const validateForm = () => {
    const newErrors = { name: '', email: '', password: '', phone: '' };

    // שם
    if (!formData.name) newErrors.name = 'שדה חובה';
    else if (formData.name.length < 2) newErrors.name = 'השם חייב להכיל לפחות 2 תווים';

    // אימייל
    if (!formData.email) newErrors.email = 'שדה חובה';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'כתובת אימייל לא תקינה';
    else if (user?.email && formData.email !== user.email) {
      // אם יש session – האימייל חייב להיות זהה ל־auth user
      newErrors.email = 'האימייל חייב להתאים לחשבון המחובר';
    }

    // סיסמה – רק כשאין session
    if (!user) {
      if (!formData.password) newErrors.password = 'שדה חובה';
      else if (formData.password.length < 6) newErrors.password = 'הסיסמה חייבת להכיל לפחות 6 תווים';
      else if (!isStrongPassword(formData.password))
        newErrors.password = 'הסיסמה חייבת לכלול אות גדולה, אות קטנה, מספר ותו מיוחד';
    }

    // טלפון
    if (!formData.phone) newErrors.phone = 'שדה חובה';
    else if (!/^05\d{8}$/.test(formData.phone)) newErrors.phone = 'מספר טלפון לא תקין';

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ✅ אכיפת אישור מדיניות לפני מעבר לשלב הבא
    if (!policyAccepted) {
      toast.error('יש לאשר את מדיניות הפרטיות לפני המשך ההרשמה');
      return;
    }

    if (!validateForm() || loading) {
      if (!user) {
        if (!isStrongPassword(formData.password)) {
          toast.error('הסיסמה חייבת לכלול אות גדולה, אות קטנה, מספר ותו מיוחד');
        } else if (formData.password.length < 6) {
          toast.error('הסיסמה חייבת להכיל לפחות 6 תווים');
        }
      }
      return;
    }

    try {
      // אם אין session – נוודא שאין כפילות אימייל ב-public.users (לא ב-auth!)
      if (!user) {
        const { data: users, error: usersError } = await supabase
          .from('users')
          .select('id')
          .eq('email', formData.email)
          .limit(1);

        if (usersError) throw usersError;

        if (users && users.length > 0) {
          setErrors(prev => ({ ...prev, email: 'כתובת האימייל כבר קיימת במערכת' }));
          return;
        }
      } else {
        // יש session – נוודא שהאימייל בטופס תואם ל-auth
        if (formData.email !== user.email) {
          setErrors(prev => ({ ...prev, email: 'האימייל חייב להתאים לחשבון המחובר' }));
          return;
        }
      }

      // אם אין session – נרשום את המשתמש ב-Auth עכשיו
      if (!user && formData.password) {
        console.log('🔵 [BusinessStepOne] Creating auth user with name:', formData.name);
        console.log('🔵 [BusinessStepOne] Full form data:', formData);

        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            data: {
              full_name: formData.name,
              name: formData.name,
              phone: formData.phone,
            }
          }
        });

        console.log('🔵 [BusinessStepOne] Auth signup result:', authData);

        if (authError) {

          if (authError.message.includes('already registered')) {
            setErrors(prev => ({ ...prev, email: 'כתובת האימייל כבר קיימת במערכת' }));
          } else {
            toast.error('שגיאה ברישום: ' + authError.message);
          }
          return;
        }

        if (!authData.user?.id) {
          toast.error('שגיאה ברישום - לא התקבל מזהה משתמש');
          return;
        }

        // יצירת רשומה בטבלת users
        const { error: userInsertError } = await supabase
          .from('users')
          .insert({
            id: authData.user.id,
            email: formData.email,
            phone: formData.phone,
            role: 'admin',
            completed: 0,
            metadata: {
              full_name: formData.name
            }
          });

        if (userInsertError) {

          // לא חוסמים את המשך ההרשמה - ננסה שוב בשלב 4
        } else {

        }
      }

      await updateStep(1, {
        name: formData.name,
        email: formData.email,
        password: user ? '' : formData.password,
        phone: formData.phone
      });

      // ✅ שמירת ההסכמה מקומית (רישום ל-DB יבוצע אחרי שיש user.id)
      setLocalConsent();

      // ✅ עדכון URL לשלב הבא
      navigate('/register/business?step=2', { replace: true });

      toast.success('הנתונים נשמרו בהצלחה! ממשיך לשלב הבא...');
    } catch (error: any) {

      toast.error(error.message || 'שגיאה בשמירת הנתונים');
    }
  };

  // התחברות/הרשמה מהירה עם Google → תמיד מפנה ל־/auth/callback
  const handleGoogleSignup = async () => {
    // ✅ חסימה אם אין אישור מדיניות
    if (!policyAccepted) {
      toast.error('יש לאשר את מדיניות הפרטיות לפני המשך ההרשמה');
      return;
    }

    try {
      // ✅ שמירת ההסכמה מראש כדי שנוכל לשחזר אחרי OAuth
      setLocalConsent();

      const configured = import.meta.env.VITE_AUTH_REDIRECT_URL;
      const fallback =
        typeof window !== 'undefined'
          ? `${window.location.origin}/auth/callback`
          : undefined;
      const redirectTo = configured || fallback;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          flowType: 'pkce'
        }
      });
      if (error) throw error;
      // אין navigate ידני – Supabase יבצע redirect. לאחר החזרה,
      // AuthCallback יוכל לעדכן DB של ההסכמה (כשהמשתמש כבר קיים).
    } catch (error: any) {

      toast.error(error.message || 'שגיאה בהתחברות עם גוגל');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        {/* שם מלא */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            שם מלא
          </label>
          <div className="relative">
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, name: e.target.value }));
                if (errors.name) setErrors(prev => ({ ...prev, name: '' }));
              }}
              className={`block w-full pl-3 pr-10 py-3 text-right border ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              } rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors`}
              placeholder="הזן את שמך המלא"
            />
            <User
              className={`absolute top-1/2 right-3 -translate-y-1/2 h-5 w-5 ${
                errors.name ? 'text-red-400' : 'text-gray-400'
              }`}
            />
          </div>
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>

        {/* אימייל */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            אימייל
          </label>
          <div className="relative">
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => {
                const value = e.target.value;
                setFormData(prev => ({ ...prev, email: value }));
                if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
              }}
              disabled={!!user} // נעול כשיש session
              className={`block w-full pl-3 pr-10 py-3 text-right border ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              } rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors ${
                user ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
              placeholder="הזן את כתובת האימייל שלך"
              dir="ltr"
            />
            <Mail
              className={`absolute top-1/2 right-3 -translate-y-1/2 h-5 w-5 ${
                errors.email ? 'text-red-400' : 'text-gray-400'
              }`}
            />
          </div>
          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
        </div>

        {/* טלפון */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            טלפון נייד
          </label>
          <div className="relative">
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                setFormData(prev => ({ ...prev, phone: value }));
                if (errors.phone) setErrors(prev => ({ ...prev, phone: '' }));
              }}
              className={`block w-full pl-3 pr-10 py-3 text-right border ${
                errors.phone ? 'border-red-300' : 'border-gray-300'
              } rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors`}
              placeholder="הזן מספר טלפון (לדוגמה: 0501234567)"
              dir="ltr"
            />
            <Phone
              className={`absolute top-1/2 right-3 -translate-y-1/2 h-5 w-5 ${
                errors.phone ? 'text-red-400' : 'text-gray-400'
              }`}
            />
          </div>
          {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
        </div>

        {/* סיסמה – מוצג רק כשאין session */}
        {!user && (
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              סיסמה
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={formData.password}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, password: e.target.value }));
                  if (errors.password) setErrors(prev => ({ ...prev, password: '' }));
                }}
                className={`block w-full pl-10 pr-10 py-3 text-right border ${
                  errors.password ? 'border-red-300' : 'border-gray-300'
                } rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors`}
                placeholder="הזן סיסמה"
                dir="ltr"
              />
              <Lock
                className={`absolute top-1/2 right-3 -translate-y-1/2 h-5 w-5 ${
                  errors.password ? 'text-red-400' : 'text-gray-400'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
          </div>
        )}
      </div>

      {/* ✅ צ׳קבוקס מדיניות – חובה */}
      <div className="space-y-1">
        <PrivacyConsent checked={policyAccepted} onChange={setPolicyAccepted} />
        {/* <p className="text-[11px] text-gray-500">גרסת מדיניות: {POLICY_VERSION}</p> */}
      </div>

      <motion.button
        type="submit"
        disabled={loading || !policyAccepted}
        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        title={!policyAccepted ? 'יש לאשר מדיניות לפני המשך ההרשמה' : undefined}
      >
        {loading ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          'המשך לשלב הבא'
        )}
      </motion.button>

      {/* קו מפריד */}
      {!user && (
        <>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-2 text-gray-400">או</span>
            </div>
          </div>

          <motion.button
            type="button"
            onClick={handleGoogleSignup}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 border border-gray-300 rounded-xl shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            disabled={!policyAccepted}
            title={!policyAccepted ? 'יש לאשר מדיניות לפני המשך ההרשמה' : undefined}
          >
            {/* אייקון גוגל */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="h-5 w-5" style={{ marginLeft: 4 }}>
              <g>
                <path fill="#4285F4" d="M24 9.5c3.54 0 6.73 1.22 9.24 3.22l6.93-6.93C36.36 2.18 30.53 0 24 0 14.64 0 6.27 5.48 1.91 13.44l8.06 6.27C12.44 13.02 17.77 9.5 24 9.5z"/>
                <path fill="#34A853" d="M46.09 24.56c0-1.64-.15-3.22-.43-4.75H24v9.02h12.44c-.54 2.92-2.16 5.39-4.6 7.06l7.13 5.55C43.73 37.73 46.09 31.68 46.09 24.56z"/>
                <path fill="#FBBC05" d="M9.97 28.71c-1.13-3.36-1.13-6.97 0-10.33L1.91 12.11C.32 16.36 0 20.09 0 24c0 3.91.32 7.64 1.91 11.09l8.06-6.27z"/>
                <path fill="#EA4335" d="M24 48c6.53 0 12.36-2.18 16.93-5.97L39.8 36.48c-2.01 1.35-4.57 2.15-7.8 2.15-6.23 0-11.56-3.52-14.03-8.21l-8.06 6.27C6.27 42.52 14.64 48 24 48z"/>
                <path fill="none" d="M0 0h48v48H0z"/>
              </g>
            </svg>
            הרשמה מהירה עם Google
          </motion.button>
        </>
      )}

      {/* כפתור "יש לי כבר חשבון" */}
      <div className="text-center mt-4">
        <button
          type="button"
          onClick={async () => {
            try {
              await supabase.auth.signOut();
              navigate('/login');
              toast.success('מנווט למסך התחברות');
            } catch (error: any) {
              toast.error('שגיאה בניקוי הסשן');
            }
          }}
          className="text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
        >
          יש לי כבר חשבון? התחבר כאן
        </button>
      </div>
    </form>
  );
}
